import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { ProductSelectionPage } from '@unbridaled/search-sdk'
import StoreFunctions from 'stores/storeFunctions'
import { Preloader } from 'lib/components/preloader'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import logger from 'utils/logger'
import { DiamondsPageState, Diamond } from 'types/interfaces'
import { useAppContext } from 'lib/context/AppContext'
import { Roles } from 'constants/roles'
import { getSearchSdkLocale } from 'i18n'

export const DiamondsPage: React.FC<any> = props => {
  const { diamondStore, appConfig, currencyInfo, typeLabels } = useAppContext()
  const history = useHistory()
  const storeFunctions = new StoreFunctions()
  const [role] = useState(storeFunctions.getUserRole())
  const [loaded, setLoaded] = useState(false)
  const [gems, setGems] = useState(null)
  const [v360PlayerUrl, setV360PlayerUrl] = useState('')
  const [isMemoMode] = useState(storeFunctions.isMemoMode())
  const [config] = useState({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  })
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<DiamondsPageState['error'] | null>(null)
  const [selectedGems, setSelectedGems] = useState<DiamondsPageState['selectedGems']>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)

  const [shopName, setShopName] = useState('')

  const fetchGems = async (): Promise<void | null> => {
    try {
      const path = window.location.pathname
      let selectedDiamondsIds: string | null = null
      if (path.length) {
        const idsHash = path.split('/')[4]
        if (idsHash) {
          try {
            selectedDiamondsIds = atob(idsHash)
          } catch (e) {
            logger.info(e)
          }
        }
        if (selectedDiamondsIds) {
          const diamondIds = selectedDiamondsIds.split(',')
          try {
            const body: { [key: string]: any } = {
              filters: {
                sku: diamondIds
              },
              include_unavailable: true
            }
            if (role !== Roles.Merchant && !isMemoMode) {
              body.currency_code = currencyInfo.currency
            } else {
              body.currency_code = undefined
            }
            const data = await diamondStore.getDiamond(
              body,
              'search',
              role
            )
            const diamonds = data.gems.map((gem: Diamond) => {
              if (gem.quantity == 0) {
                return {
                  ...gem,
                  wholesale_price: 0
                }
              }
              return gem
            })
            setGems(diamonds)
            setLoaded(true)
          } catch (e) {
            logger.info(e)
          }
        }
      }
    } catch (e) {
      logger.info(e)
      return null
    }
  }

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  useEffect(() => {
    fetchShopName()
    fetchGems()
  }, [])

  useLayoutEffect(() => {
    setV360PlayerUrl(`${process.env.VIEWER_URL}/api/360player`)
  }, [])

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const handleAddToCart = async (gems: { [key: string]: any }[]): Promise<void> => {
    const result = await storeFunctions.addToCart(gems)
    if (!result.success) {
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    window.location.href = '/cart'
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: "diamond_search"
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const handleDASOrdering = (diamondsData: { [key: string]: any }, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  return (
    <>
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onClose={closeDASOrderSuccessfullModal}
      />
      {loaded ? (
        <div className="unbridaled-curated-diamonds">
          <ProductSelectionPage
            locale={getSearchSdkLocale()}
            shareProps={{ hidden: true }}
            role={role}
            data={{
              products: gems
            }}
            isActivated={true}
            isOnboarded={true}
            {...(config.advancedSearchCartEnabled ? { onAddCartItem: handleAddToCart } : {})}
            {...(isMemoMode
              ? {
                  onAddInStoreItem: (item: { [key: string]: any }): void =>
                    handleDASOrdering(item, 'i'),
                  onAddMemoItem: (item: { [key: string]: any }): void =>
                    handleDASOrdering(item, 'm')
                }
              : {})}
            onAddJewelryItem={(item: { [key: string]: any }): void => {
              const searchParams = new URLSearchParams(window.location.search)
              const jType = searchParams.get('jewelry_type')
              const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
              history.push('/apps/ub/diamond_collection/' + item.sku + jewelryType)
            }}
            {...(process.env.VIEWER_URL ? { viewerUrl: process.env.VIEWER_URL } : {})}
            v360PlayerUrl={v360PlayerUrl}
            nativeV360PlayerUrl={`${location.origin}/apps/ub/360player`}
            getIsJewelryInStock={async (data: { [key: string]: any }): Promise<boolean> => {
              try {
                const productIds = await storeFunctions.tagProductsForSKU([data])
                if (productIds) {
                  return true
                }
                return false
              } catch (e) {
                return false
              }
            }}
            currencyProps={{
              currencies: currencyInfo.currencies,
              currencyCode: currencyInfo.currency,
              currencyRate: currencyInfo.currencyRate
            }}
            labels={typeLabels}
            ringSizeProps={{
              sizeRegion: config.ringSizeRegion
            }}
          />
        </div>
      ) : (
        <Preloader preloaderClass="unbridaled-full-page-preloader" />
      )}
    </>
  )
}

export default DiamondsPage
